/* LLO "Watch the Series" player (sections/llo-watch-series.liquid).
 * One <video> element plays the playlist in order: episode -> its in-between short -> next episode.
 * Episodes without a video are not in the playlist (they render as "Coming soon" cards).
 * Starts muted (autoplay-safe); the sound toggle, a tap on the video, or any Play/Watch-it-all button unmutes.
 * Phones in portrait (< 1025px wide) get the vertical file, everything else the 16:9 file. */
(() => {
  if (customElements.get('llo-watch-player')) return;
  const VERTICAL = '(orientation: portrait) and (max-width: 1024px), (max-width: 749px)';
  class LloWatchPlayer extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.items = [...this.querySelectorAll('[data-item]')].map((li) => ({ ...li.dataset }));
      if (!this.video || !this.items.length) return;
      this.mq = window.matchMedia(VERTICAL);
      this.idx = 0;
      this.soundBtn = this.querySelector('[data-action="sound"]');
      this.bigPlay = this.querySelector('[data-action="bigplay"]');
      this.endCard = this.querySelector('[data-end]');
      this.now = this.querySelector('[data-now]');
      this.upnext = this.querySelector('[data-upnext]');
      this.bar = this.querySelector('[data-progress]');
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.addEventListener('click', (e) => {
        const a = e.target.closest('[data-action]');
        if (!a) return;
        const act = a.dataset.action;
        if (act === 'sound') this.setSound(this.video.muted);
        else if (act === 'tap') this.onTap();
        else if (act === 'bigplay') { this.setSound(true); this.play(); }
        else if (act === 'all') { this.start(0, true); }
        else if (act === 'next') { this.start(this.idx + 1, false); }
      });
      document.querySelectorAll(`#llo-watch-${this.dataset.section} [data-play]`).forEach((b) =>
        b.addEventListener('click', () => {
          const i = this.items.findIndex((it) => it.item === 'episode' && it.block === b.dataset.play);
          if (i > -1) { this.start(i, true); this.scrollIntoView({ behavior: this.reduced ? 'auto' : 'smooth', block: 'start' }); }
        })
      );
      this.video.addEventListener('ended', () => this.start(this.idx + 1, false));
      this.video.addEventListener('timeupdate', () => {
        const d = this.video.duration || 0;
        this.bar.style.width = d ? `${(100 * this.video.currentTime) / d}%` : '0';
      });
      this.video.addEventListener('error', () => {
        // Original upload failed: fall back to Shopify's MP4 transcode once.
        const it = this.items[this.idx], o = this.orient();
        const fb = it[`${o}Fb`] || it[`${o === 'v' ? 'h' : 'v'}Fb`];
        if (fb && this.video.getAttribute('src') !== fb) { this.video.src = fb; this.play(); }
      });
      this.mq.addEventListener('change', () => this.load(true));
      // Pause when scrolled fully out of view, resume when back.
      new IntersectionObserver((es) => es.forEach((e) => {
        if (!e.isIntersecting && !this.video.paused && Date.now() > (this.holdIO || 0)) { this.offscreenPaused = true; this.video.pause(); }
        else if (e.isIntersecting && this.offscreenPaused) { this.offscreenPaused = false; this.play(); }
      }), { threshold: 0 }).observe(this.video);

      // Deep link: ?ep=4 or #ep-4
      const q = new URLSearchParams(location.search).get('ep') || (location.hash.match(/^#ep-(\d+)/) || [])[1];
      const start = q ? Math.max(0, this.items.findIndex((it) => it.item === 'episode' && it.ep === String(q))) : 0;
      this.idx = start;
      this.load(false);
      this.setSound(false, true);
      if (this.reduced) this.showBigPlay(); else this.play();
    }
    orient() { return this.mq.matches ? 'v' : 'h'; }
    load(keepTime) {
      const it = this.items[this.idx]; if (!it) return;
      const o = this.orient(), alt = o === 'v' ? 'h' : 'v';
      this.dataset.orient = it[`${o}Src`] ? o : alt;
      const src = it[`${o}Src`] || it[`${alt}Src`];
      const poster = it[`${o}Poster`] || '';
      const t = keepTime ? this.video.currentTime : 0, wasPlaying = keepTime && !this.video.paused;
      if (this.video.getAttribute('src') !== src) {
        this.video.poster = poster;
        this.video.src = src;
        if (t) this.video.addEventListener('loadedmetadata', () => { this.video.currentTime = t; }, { once: true });
        if (wasPlaying) this.play();
      }
      this.endCard.hidden = true;
      this.now.textContent = it.label;
      const nx = this.items[this.idx + 1];
      this.upnext.textContent = nx ? `Up next: ${nx.label}` : '';
      document.querySelectorAll(`#llo-watch-${this.dataset.section} [data-shop-for]`).forEach((p) => { p.hidden = p.dataset.shopFor !== it.block; });
      document.querySelectorAll(`#llo-watch-${this.dataset.section} [data-card]`).forEach((c) => c.classList.toggle('is-playing', c.dataset.card === it.block));
      // Warm the next file.
      if (nx) { const n = nx[`${o}Src`] || nx[`${alt}Src`]; if (n && this.prefetched !== n) { this.prefetched = n; const l = document.createElement('link'); l.rel = 'prefetch'; l.as = 'video'; l.href = n; document.head.appendChild(l); } }
    }
    start(i, withSound) {
      if (i >= this.items.length) { this.finish(); return; }
      this.idx = Math.max(0, i);
      this.holdIO = Date.now() + 2500; // user just asked to play; let the scroll into view finish
      this.offscreenPaused = false;
      if (withSound) this.setSound(true);
      this.load(false);
      this.play();
    }
    finish() {
      this.video.pause();
      this.endCard.hidden = false;
      this.now.textContent = '';
      this.upnext.textContent = '';
    }
    play() {
      this.bigPlay.hidden = true;
      const p = this.video.play();
      if (p && p.catch) p.catch((err) => {
        if (err && err.name === 'AbortError') return; // interrupted by a new src or a pause, not an autoplay block
        if (!this.video.muted) { this.setSound(false, true); this.video.play().catch(() => this.showBigPlay()); }
        else this.showBigPlay();
      });
    }
    showBigPlay() { this.bigPlay.hidden = false; }
    onTap() {
      if (this.video.muted) { this.setSound(true); if (this.video.paused) this.play(); }
      else if (this.video.paused) this.play();
      else this.video.pause();
    }
    setSound(on, silent) {
      this.video.muted = !on;
      if (on) this.video.removeAttribute('muted'); else this.video.setAttribute('muted', '');
      this.soundBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
      this.querySelector('[data-action="tap"]').setAttribute('aria-label', on ? 'Pause or play' : 'Tap for sound');
      if (!silent) sessionStorage.setItem('llo-watch-sound', on ? '1' : '0');
    }
  }
  customElements.define('llo-watch-player', LloWatchPlayer);
})();
